import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
  useEffectEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authApi, invalidateMeCache } from '@/api/auth.api';
import { invitationsApi } from '@/api/invitations.api';
import {
  clearSessionStorage,
  hasStoredSession,
  markSessionStarted,
  consumeReturnTo,
  rememberReturnTo,
} from '@/lib/session';
import type { AuthState, LoginCredentials, Clinic } from '@/types/auth.types';

const isServerError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    // No response = network error / CORS-blocked request / gateway down
    if (!status) return true;
    // 5xx = server-side error
    if (status >= 500) return true;
    return false;
  }
  // Non-axios errors (TypeError from fetch, etc.) are also network-level
  if (error instanceof TypeError) return true;
  return false;
};

interface AuthContextType extends AuthState {
  login: (
    credentials: LoginCredentials,
    invitationToken?: string,
  ) => Promise<void>;
  register: (userData: {
    name: string;
    email: string;
    password: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  /** Resolves false when the staff member is deactivated there and was sent to /deactivated. */
  selectClinic: (clinic: Clinic) => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  /** Merge partial fields into the current in-memory clinic (e.g. after a settings save). */
  updateClinic: (partial: Partial<Clinic>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    clinic: null,
    permissions: [],
    isAuthenticated: false,
    isLoading: true,
  });

  // Guard to prevent simultaneous checkAuth calls (useRef avoids stale closure issue)
  const isCheckingAuthRef = useRef(false);

  // Track navigation timeouts for cleanup
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Cleanup navigation timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const checkOnMount = useEffectEvent(() => checkAuth());

  // Check authentication on mount
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      if (isMounted) {
        await checkOnMount();
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []); // Only run once on mount

  const checkAuth = async (): Promise<boolean> => {
    // Prevent simultaneous calls (ref is synchronous, no stale closure)
    if (isCheckingAuthRef.current) {
      return authState.isAuthenticated;
    }

    // On public pages there's usually nothing to verify — but a user who is still
    // signed in can reach /login directly (typed URL, bookmark, hard refresh), and
    // PublicRoute can only bounce them to the dashboard if auth state is resolved
    // here. So probe when a previous session left a hint, and skip otherwise: a
    // genuine visitor still costs zero API calls and sees the form immediately.
    //
    // /reset-password is opened from an emailed token link and never redirects an
    // authenticated user, so it stays a hard skip.
    const onPublicPage = [
      '/login',
      '/reset-password',
      '/forgot-password',
      '/signup',
      '/consent/sign',
      '/plan/review',
      '/medical-form',
    ].some((p) => window.location.pathname.includes(p));
    const onResetPassword =
      window.location.pathname.includes('/reset-password');
    if (onPublicPage && (!hasStoredSession() || onResetPassword)) {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      return false;
    }

    isCheckingAuthRef.current = true;
    try {
      // Try GET /auth/me — let the interceptor transparently refresh the access
      // token if it expired (skipRefresh=false so the refresh interceptor fires).
      // This is the main case that caused post-deploy logouts: AT expired but RT
      // was still valid, yet checkAuth was bypassing the refresh interceptor.
      try {
        // (skipRefresh=false, allowRefreshOnPublic=true) — the second flag lets the
        // probe refresh an expired access token even on /login, which is the whole
        // point of running it there.
        const [meResult, clinics] = await Promise.all([
          authApi.getMe(false, true),
          authApi.getUserClinics(false, true),
        ]);
        const { user, permissions } = meResult;
        const currentClinic =
          clinics.find((o) => o.id === (user.clinic ?? user.clinicId)) ?? null;

        // Keep the persisted clinic id in sync with the JWT so a subsequent hard
        // refresh doesn't bounce to clinic-selection before this resolves.
        if (currentClinic) {
          localStorage.setItem('selectedClinicId', currentClinic.id);
        }
        // Cookies verified — (re)assert the session flag, which also upgrades
        // sessions that predate it.
        markSessionStarted();

        setAuthState({
          user,
          clinic: currentClinic,
          permissions: permissions ?? [],
          isAuthenticated: true,
          isLoading: false,
        });

        // Block deactivated staff members
        if (user.isActive === false) {
          navigate('/deactivated', {
            state: { clinicName: currentClinic?.name },
          });
          isCheckingAuthRef.current = false;
          return false;
        }

        // Only redirect if explicitly false (not undefined/null) and not already on verify-email
        if (
          user.isEmailVerified === false &&
          window.location.pathname !== '/verify-email'
        ) {
          navigate('/verify-email', {
            state: { email: user.email, autoSend: false },
          });
        }

        isCheckingAuthRef.current = false;
        return true;
      } catch (err) {
        // Re-throw server/network errors — don't treat an unreachable server as "not logged in"
        if (isServerError(err)) throw err;
        // /auth/me 401 (after refresh attempt) = JWT might not have an clinic claim yet
      }

      const isOnPublicPage =
        window.location.pathname.includes('/login') ||
        window.location.pathname === '/' ||
        window.location.pathname.includes('/forgot-password') ||
        window.location.pathname.includes('/reset-password') ||
        window.location.pathname.includes('/signup') ||
        window.location.pathname.includes('/consent/sign') ||
        window.location.pathname.includes('/plan/review') ||
        window.location.pathname.includes('/medical-form');

      if (!isOnPublicPage) {
        // Come back to the page being opened once a clinic is picked.
        rememberReturnTo(window.location.pathname + window.location.search);
        try {
          // Use skipRefresh=true here: if /auth/me already failed after a refresh
          // attempt, we only want to check if there's a bare refresh token (no clinic claim).
          const clinics = await authApi.getUserClinics(true);

          setAuthState({
            user: null,
            clinic: null,
            permissions: [],
            isAuthenticated: true,
            isLoading: false,
          });

          if (clinics.length === 1) {
            await selectClinic(clinics[0]);
            if (navigationTimeoutRef.current)
              clearTimeout(navigationTimeoutRef.current);
            navigationTimeoutRef.current = setTimeout(
              () => navigate(consumeReturnTo()),
              100,
            );
          } else if (clinics.length > 1) {
            navigate('/select-clinic', { state: { clinics } });
          }

          isCheckingAuthRef.current = false;
          return true;
        } catch (err) {
          // Re-throw server/network errors — don't treat an unreachable server as "not logged in"
          if (isServerError(err)) throw err;
          // 401 here = no valid cookies, fall through to unauthenticated
        }
      }

      // Definitively signed out (401, not a server error). Drop the session flag
      // so the next load skips the probe entirely instead of re-failing it.
      clearSessionStorage();
      setAuthState({
        user: null,
        clinic: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
      });
      isCheckingAuthRef.current = false;
      return false;
    } catch {
      // Server/network error — preserve current auth state, just stop loading
      // so the user isn't logged out due to a transient backend outage
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      isCheckingAuthRef.current = false;
      return authState.isAuthenticated;
    }
  };

  const login = async (
    credentials: LoginCredentials,
    invitationToken?: string,
  ) => {
    // NOTE: no try/catch here on purpose. An axios error from this call
    // carries the request body — the user's email and password — so it must
    // never be logged. Callers render a safe message from resolveAuthError().
    {
      // Login - tokens set in cookies by backend
      const response = await authApi.login({
        username: credentials.username.toLowerCase(),
        password: credentials.password,
      });

      // Mark as authenticated. Seed `user` from the login response too: the
      // full record comes from /auth/me, but that needs an clinic claim
      // in the JWT, which an account still sitting on an invitation does not
      // have. Without this, every screen reachable before an clinic is
      // chosen sees `user: null` and cannot act on the person's own identity.
      setAuthState((prev) => ({
        ...prev,
        user: response.user
          ? {
              ...prev.user,
              id: response.user.id,
              email: response.user.email,
              name: response.user.name,
              // The login field is the email address — there is no separate
              // username in this system.
              username: response.user.email,
              isEmailVerified: response.user.isEmailVerified,
            }
          : prev.user,
        isAuthenticated: true,
        isLoading: false,
      }));

      // Session established — record it so a later visit to /login is recognised
      // and redirected instead of showing the form again.
      markSessionStarted();

      // Store remember me preference
      if (credentials.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }

      // Check if email is verified
      if (response.user && !response.user.isEmailVerified) {
        navigate('/verify-email', {
          state: { email: response.user.email, autoSend: true },
        });
        return;
      }

      // If logged in via invitation link — go to invitations page to accept/deny
      if (invitationToken) {
        navigate('/invitations');
        return;
      }

      // Get user's clinics
      const clinics = await authApi.getUserClinics();

      if (clinics.length === 1) {
        // Auto-select if only one clinic
        await selectClinic(clinics[0]);
        if (navigationTimeoutRef.current)
          clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = setTimeout(
          () => navigate(consumeReturnTo()),
          100,
        );
      } else if (clinics.length > 1) {
        navigate('/select-clinic', { state: { clinics } });
      } else {
        // The credentials were fine — this account just belongs to no clinic
        // yet. Throwing here sent a bare Error back to the login screen, which
        // maps anything it does not recognise onto "that email or password is
        // incorrect": the one message that is definitely wrong, on the one
        // screen that cannot act on it. Route to the reason instead. Nearly
        // always that reason is an invitation nobody accepted; when there is
        // none, /select-clinic already owns the "you are not part of any
        // clinic yet" screen.
        const pending = await invitationsApi
          .getMyInvitations()
          .catch(
            () =>
              [] as Awaited<ReturnType<typeof invitationsApi.getMyInvitations>>,
          );
        navigate(
          pending?.some((inv) => inv.status === 'pending')
            ? '/invitations'
            : '/select-clinic',
          { state: { clinics } },
        );
      }
    }
  };

  const selectClinic = async (clinic: Clinic) => {
    try {
      // Re-issue JWT with this clinic embedded in the token
      await authApi.selectClinic(clinic.id);

      // Persist the choice so a hard refresh doesn't bounce the user back to
      // the clinic-selection screen before checkAuth has re-hydrated state.
      // (ProtectedRoute reads this key; the JWT cookie remains the source of
      // truth for the backend.)
      localStorage.setItem('selectedClinicId', clinic.id);

      // JWT changed — must fetch fresh user+permissions, not cached
      invalidateMeCache();
      const { user, permissions } = await authApi.getMe();

      // Block if deactivated in this clinic
      if (user.isActive === false) {
        navigate('/deactivated', { state: { clinicName: clinic.name } });
        return false;
      }

      setAuthState({
        user,
        clinic: clinic,
        permissions: permissions ?? [],
        isAuthenticated: true,
        isLoading: false,
      });
      return true;
    } catch (error: unknown) {
      // If deactivated, the backend throws 403 with code STAFF_DEACTIVATED
      const axiosError = error as { response?: { data?: { code?: string } } };
      if (axiosError?.response?.data?.code === 'STAFF_DEACTIVATED') {
        navigate('/deactivated', { state: { clinicName: clinic.name } });
        return false;
      }
      console.error('selectClinic error:', error);
      throw error;
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    password: string;
  }) => {
    // Same reason as login: the axios error carries the posted password.
    {
      // Register - tokens set in cookies by backend
      const response = await authApi.register(userData);

      // Mark as authenticated
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false,
      }));

      // Check if email is verified
      if (response.user && !response.user.isEmailVerified) {
        // Navigate to verification, OTP will be sent on page
        navigate('/verify-email', {
          state: { email: response.user.email, autoSend: true },
        });
        return;
      }

      // Get user's clinics
      const clinics = await authApi.getUserClinics();

      if (clinics.length === 1) {
        // Auto-select if only one clinic
        await selectClinic(clinics[0]);
        // Small delay to ensure state is updated before navigation
        if (navigationTimeoutRef.current)
          clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = setTimeout(
          () => navigate(consumeReturnTo()),
          100,
        );
      } else {
        // Navigate to clinic selection
        navigate('/select-clinic', { state: { clinics } });
      }
    }
  };

  const logout = async () => {
    try {
      // Unregister FCM token before clearing session
      const { unregisterFcmToken } = await import('@/services/fcm.service');
      await unregisterFcmToken();
    } catch {
      // non-critical
    }
    invalidateMeCache();
    try {
      // Backend will clear cookies
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear auth state
      clearSessionStorage();

      setAuthState({
        user: null,
        clinic: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
      });

      navigate('/login');
    }
  };

  // Merge updated fields into the in-memory clinic so changes made in
  // settings (e.g. timezone) take effect immediately without a re-login.
  const updateClinic = (partial: Partial<Clinic>) => {
    setAuthState((prev) =>
      prev.clinic ? { ...prev, clinic: { ...prev.clinic, ...partial } } : prev,
    );
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        register,
        logout,
        selectClinic,
        checkAuth,
        updateClinic,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
